import React, { useEffect, useState } from "react";
import Base from "../core/Base";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, IconButton, Button,
  TextField, InputAdornment, Tooltip, Stack, Alert, Chip,
} from "@mui/material";
import SearchIcon     from "@mui/icons-material/Search";
import EditIcon       from "@mui/icons-material/Edit";
import DeleteIcon     from "@mui/icons-material/Delete";
import PersonAddIcon  from "@mui/icons-material/PersonAdd";
import PlayArrowIcon  from "@mui/icons-material/PlayArrow";
import PauseIcon      from "@mui/icons-material/Pause";
import { Link }       from "react-router-dom";
import { isAutheticated } from "../auth/helper";
import { deleteSubAdmin, getAllSubAdmins, activateSubAdmin, deactivateSubAdmin } from "./helper/adminapicall";

const Subadmins = () => {
  const { admin, token } = isAutheticated();
  const [subadmins, setSubadmins] = useState([]);
  const [filtered,  setFiltered]  = useState([]);
  const [search,    setSearch]    = useState("");
  const [page,      setPage]      = useState(0);
  const rowsPerPage = 10;
  const [msg, setMsg] = useState({ text: "", severity: "success" });

  const load = () =>
    getAllSubAdmins(admin._id, token).then((data) => {
      const list = Array.isArray(data) ? data : [];
      setSubadmins(list); setFiltered(list);
    });

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(subadmins.filter((s) => s.name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q)));
    setPage(0);
  }, [search, subadmins]);

  const flash = (text, severity = "success") => {
    setMsg({ text, severity });
    setTimeout(() => setMsg({ text: "" }), 4000);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this subadmin?")) return;
    deleteSubAdmin(admin._id, token, id)
      .then(() => { flash("Subadmin deleted"); load(); })
      .catch(() => flash("Delete failed", "error"));
  };

  const handleToggleActive = (s) => {
    const fn = s.isActive === false ? activateSubAdmin : deactivateSubAdmin;
    fn(admin._id, token, s._id)
      .then(() => load())
      .catch(() => flash("Failed to update status", "error"));
  };

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Base>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5">Subadmins</Typography>
          <Typography variant="body2" color="text.secondary">{subadmins.length} total subadmins</Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAddIcon />} component={Link} to="/add/subadmin">
          Add Subadmin
        </Button>
      </Box>

      {msg.text && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg({ text: "" })}>{msg.text}</Alert>}

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
        <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
          <TextField
            size="small" placeholder="Search by name or email..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "text.secondary", fontSize: 18 }} /></InputAdornment> }}
            sx={{ width: 300 }}
          />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Name", "Email", "Phone", "Status", "Page Access", "Created", "Actions"].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((s) => (
                <TableRow key={s._id} hover sx={{ "&:hover": { bgcolor: "rgba(255,255,255,0.03)" } }}>
                  <TableCell sx={{ fontWeight: 500 }}>{s.name} {s.lastname}</TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: "0.8rem" }}>{s.email}</TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: "0.8rem" }}>{s.phone || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      label={s.isActive === false ? "Inactive" : "Active"}
                      size="small"
                      color={s.isActive === false ? "default" : "success"}
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  </TableCell>
                  <TableCell>
                    {Array.isArray(s.allowedPages) && s.allowedPages.length > 0 ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {s.allowedPages.slice(0, 3).map((p) => (
                          <Chip key={p} label={p} size="small" variant="outlined" sx={{ fontSize: "0.68rem" }} />
                        ))}
                        {s.allowedPages.length > 3 && (
                          <Chip label={`+${s.allowedPages.length - 3}`} size="small" sx={{ fontSize: "0.68rem" }} />
                        )}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.disabled">None</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                    {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Edit">
                        <IconButton size="small" component={Link} to={`/update/subadmin/${s._id}`} sx={{ color: "primary.main" }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={s.isActive === false ? "Activate" : "Deactivate"}>
                        <IconButton size="small" onClick={() => handleToggleActive(s)}
                          sx={{ color: s.isActive === false ? "success.main" : "warning.main" }}>
                          {s.isActive === false ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(s._id)} sx={{ color: "error.main" }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {paginated.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No subadmins found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={filtered.length} page={page}
          rowsPerPage={rowsPerPage} rowsPerPageOptions={[10]}
          onPageChange={(_, p) => setPage(p)}
          sx={{ borderTop: "1px solid", borderColor: "divider" }}
        />
      </Paper>
    </Base>
  );
};

export default Subadmins;
